// src/features/caas/hooks/useCaasClients.ts
import { useState, useEffect, useCallback } from 'react';
import { CaasClient } from '../../../types';
import { listClients } from '../../../services/api/endpoints/caas';
import { useAuth } from '../../../contexts/AuthContext';

interface UseCaasClientsResult {
  clients: CaasClient[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useCaasClients = (): UseCaasClientsResult => {
  const { user } = useAuth();
  const [clients, setClients] = useState<CaasClient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = useCallback(async () => {
    if (!user?.token) {
      setError('No authentication token available');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await listClients(user.token);
      setClients(data);
    } catch (err: any) {
      console.error('Error fetching CaaS clients:', err);
      setError(err.message || 'Failed to load CaaS clients');
    } finally {
      setIsLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return {
    clients,
    isLoading,
    error,
    refresh: fetchClients,
  };
};
